import { omit, capitalize } from 'lodash';
import { v4 as uuid } from 'uuid';
import { genAttributes, ORDER_READONLY_FIELDS } from '@onward-delivery/core';
import { buildServicesSavePayload } from './saveServices';
import { buildDocumentsSavePayload } from './saveDocuments';
import { buildItemsSavePayload } from './saveItems';
import { pinProtectedFields } from './staleSave';

/**
 * Builds the note row the user typed into the Notes tab, or null when they typed nothing.
 * Admins write private-to-Admin notes; everyone else writes as Shipper, or as Carrier when
 * a carrier edits an assigned order that is not theirs.
 *
 * @param {{ note?: string, type?: string, notify?: boolean, private?: boolean }} noteInfo Notes tab draft.
 * @param {{ isAdmin: boolean, user_id: string, client_id: string, userType: string, isNewOrder: boolean }} author
 *   Who is saving. `user_id` is the id to stamp as `source_user_id` for this role.
 * @param {{ order: { order_id: string, carrier_id?: string, shipper_id?: string }, newId?: () => string }} context
 *   The order the note belongs to, and an id source (default `uuid`).
 * @returns {object|null} A note row for `onSave`, or null.
 */
export const buildNewNote = (noteInfo, author, { order, newId = uuid }) => {
    if (!noteInfo?.note) {
        return null;
    }
    if (author.isAdmin) {
        return {
            note: noteInfo.note,
            source_user_type: 'Admin',
            source_user_id: author.user_id,
            is_acknowledgement: false,
            requires_acknowledgement: false,
            acknowledged_by_shipper: false,
            acknowledged_by_admin: false,
            private_to: 'Admin',
            type: noteInfo.type || null,
            order_id: order.order_id,
            note_id: newId(),
        };
    }
    let source_user_type = 'Shipper';
    if (!author.isNewOrder && order?.carrier_id && author.client_id !== order?.shipper_id) {
        source_user_type = 'Carrier';
    }
    return {
        note: noteInfo.note,
        source_user_type,
        source_user_id: author.user_id,
        is_acknowledgement: false,
        requires_acknowledgement: noteInfo.notify,
        acknowledged_by_shipper: false,
        acknowledged_by_admin: false,
        private_to: noteInfo.private ? capitalize(author.userType) : null,
        type: noteInfo.type,
        note_id: newId(),
    };
};

/**
 * Builds the saved-customer row inserted when the user ticks "Save Customer".
 *
 * @param {object} clone The order being edited.
 * @param {string} client_id The saving user's client id.
 * @returns {object} The `customer` variable for `INSERT_SAVED_CUSTOMER_LOCATION`.
 */
export const savedCustomerRow = (clone, client_id) => {
    const { address, city, state, street, zip, lat, long, unit, location, location_type } = genAttributes(clone);
    return {
        client_id,
        address: clone?.[address] || null,
        business_city: clone?.[city] || null,
        business_state: clone?.[state] || null,
        business_address: clone?.[street] || null,
        business_zip: clone?.[zip] || null,
        lat: clone?.[lat] || null,
        lng: clone?.[long] || null,
        business_unit: clone?.[unit] || null,
        location_type: clone?.[location] || null,
        location_info: clone?.[location_type] || null,
        contact_name: clone?.dropoff_name || null,
        contact_phone: clone?.dropoff_phone || null,
        contact_email: clone?.dropoff_email || null,
        is_customer_location: true,
    };
};

/**
 * Builds the argument for `callbacks.onSave` (minus `errors`) from the priced order. Pure: no
 * state changes, and ids come from `newId`.
 *
 * Removal diffs (items, tags, services, references) measure against `baseOrder`, not the prop:
 * after Reload order the prop is stale, and a row that only exists in the reloaded order must
 * still be deletable.
 *
 * @param {object} args
 * @param {object} args.priced The order after distance and price.
 * @param {object} args.clone The order being edited.
 * @param {object} args.baseOrder What edits are measured against.
 * @param {object|null} args.stored Stored status fields from the guard read, or null when unguarded.
 * @param {object} args.noteInfo Notes tab draft.
 * @param {{ isAdmin: boolean, user_id: string, client_id: string, userType: string, isNewOrder: boolean }} args.author
 * @param {() => string} [args.newId] Id source for new notes (default `uuid`).
 * @returns {{ order: object, items: object[], itemsRemoved: string[], itemUpdates: object[], tags: object[],
 *   tagsRemoved: string[], documents: object[], orderIds: string[], subscriptions: object[], services: object[],
 *   servicesRemoved: string[], references: object[], referencesRemoved: string[] }}
 */
export const buildSavePayload = ({ priced, clone, baseOrder, stored, noteInfo, author, newId = uuid }) => {
    // Guard the items diff against undefined `itemsByOrderId` on either side: service-callback
    // orders carry [] today.
    const cloneItems = clone.itemsByOrderId || [];
    const cloneItemIds = cloneItems.map((item) => item.item_id);
    const itemsRemoved = (baseOrder.itemsByOrderId || [])
        .filter((item) => !cloneItemIds.includes(item.item_id))
        .map((item) => item.item_id);

    const notes = priced?.notes || [];

    // Read before omitting below so they're not lost
    const cloneDocuments = priced?.documents;
    const email_subscriptions = priced?.email_subscriptions || [];

    const { itemsByOrderId, tags = [], services: pricedServices, ...unpinned } = omit(priced, ORDER_READONLY_FIELDS);
    // Status and completion fields keep their stored values unless the user changed them here.
    const modified = pinProtectedFields({ outgoing: unpinned, base: baseOrder, stored });

    const { items, itemUpdates } = buildItemsSavePayload({
        originalItems: baseOrder.itemsByOrderId || [],
        editedItems: itemsByOrderId || [],
    });

    const modifiedTags = (tags || []).map((mapping) => {
        const { __typename, order, service_level, tag, ...rest } = mapping;
        return rest;
    });
    const keptTagIds = modifiedTags.map(({ tag_id }) => tag_id);
    const tagsRemoved = (baseOrder.tags || [])
        .filter((mapping) => !keptTagIds.includes(mapping.tag_id))
        .map((mapping) => mapping.mapping_id);

    // orderIds keys the mapping deletes in UPSERT_ORDERS. It comes back empty when the order
    // arrived without a `documents` projection, so an unknown list cannot be mistaken for
    // "detach everything".
    const { documents, orderIds } = buildDocumentsSavePayload({
        documents: cloneDocuments,
        orderId: clone.order_id,
    });

    const { services, servicesRemoved } = buildServicesSavePayload({
        pricedServices,
        cloneServices: clone.services,
        orderServices: baseOrder.services,
        orderId: clone.order_id,
    });

    const cloneRefs = clone.references || [];
    const references = cloneRefs.map((ref) => {
        const clean = omit(ref, ['__typename', 'created_at', 'last_modified_at']);
        // New rows omit reference_id so Hasura generates one.
        if (!clean.reference_id) {
            delete clean.reference_id;
        }
        return { ...clean, order_id: clone.order_id };
    });
    const keptRefIds = new Set(cloneRefs.map((r) => r.reference_id).filter(Boolean));
    const referencesRemoved = (baseOrder.references || [])
        .map((r) => r.reference_id)
        .filter((id) => id && !keptRefIds.has(id));

    const newNote = buildNewNote(noteInfo, author, { order: clone, newId });
    const modifiedNotes = (newNote ? [...notes, newNote] : [...notes]).map((note) => {
        const { __typename, order_id, user, ...rest } = note;
        return rest;
    });

    const subscriptions = email_subscriptions.map((subscription) => ({
        order_id: clone.order_id,
        event: subscription.event,
        email: subscription.email,
    }));

    return {
        order: { ...modified, notes: modifiedNotes },
        items,
        itemsRemoved,
        itemUpdates,
        tags: modifiedTags,
        tagsRemoved,
        documents,
        orderIds,
        subscriptions,
        services,
        servicesRemoved,
        references,
        referencesRemoved,
    };
};
